import styled from '@emotion/styled'
import React from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import { useTranslation } from 'react-i18next'
import { useLoaderData } from 'react-router-dom'
import useAuth from '../../hooks/useAuth'
import { useTheme } from '@emotion/react'

import { Button, Grid, LinearProgress, Divider as MuiDivider, Stack, Typography } from '@mui/material'

import { spacing } from '@mui/system'
import { normalizedWarrantName } from '@finance-lab/utils/shared'
import WarrantDetailsView from './WarrantDetailsView'
import { isFutureWarrant, isPreviousWarrant } from './WarrantsView'
import { WarrantEntity } from '../../types/WarrantEntity'
import { WatchListButton } from '../components/WatchListButton'

const Divider = styled(MuiDivider)(spacing)

function getDescriptiveName(warrant: WarrantEntity) {
    if (!warrant) return ''
    return normalizedWarrantName(warrant)
}

export function WarrantStatus({ warrant, details = false }: { warrant: WarrantEntity; details?: boolean }) {
    const theme = useTheme()
    if (!warrant) return null
    const now = new Date()

    let status: { color: string; text: string } | null = null

    if (warrant.cancelled) status = { color: theme.palette.entityStatus.cancelled, text: 'Avbruten' }
    else if (isFutureWarrant(now, warrant)) status = { color: theme.palette.entityStatus.incomplete, text: 'Kommande' }
    else if (isPreviousWarrant(now, warrant)) status = { color: theme.palette.entityStatus.complete, text: 'Genomförd' }
    else status = { color: theme.palette.entityStatus.incomplete, text: 'Pågående inlösenperiod' }

    if (!status) return null

    if (!details) return <span>{status.text}</span>

    return (
        <Stack direction='row' spacing={1} alignItems='center'>
            <div
                style={{
                    height: '10px',
                    width: '10px',
                    margin: 0,
                    backgroundColor: status.color,
                    borderRadius: '50%',
                }}
            />
            <Typography>{status.text}</Typography>
        </Stack>
    )
}

function WarrantDetails() {
    const { user } = useAuth()
    const { t } = useTranslation()
    const warrant = useLoaderData() as WarrantEntity

    return (
        <>
            <Helmet title={t('site.warrants.detailsTitle', { name: getDescriptiveName(warrant) })} />
            <Typography
                variant='h3'
                gutterBottom
                display='inline'
                sx={{ fontStyle: warrant?.cancelled ? 'italic' : 'normal' }}
            >
                {warrant?.cancelled ? 'Avbruten ' : ''}{' '}
                {t('site.warrants.detailsTitle', { name: getDescriptiveName(warrant) })}
                <WatchListButton entityId={warrant.id} isInWatchList={!!warrant.isInWatchList} type='Warrant' />
            </Typography>

            {user?.groups?.includes('superadmin') && (
                <>
                    <Button variant='text' href={`/admin/warrants/${warrant?.id}`}>
                        [Redigera]
                    </Button>
                </>
            )}

            <Typography variant='body2' display='block' gutterBottom>
                Status: <WarrantStatus warrant={warrant} />
            </Typography>

            <Divider my={6} />

            <Grid container spacing={6}>
                <Grid size={12}>
                    {!warrant && <LinearProgress />}
                    {warrant && <WarrantDetailsView warrant={warrant} />}
                </Grid>
            </Grid>
        </>
    )
}

export default WarrantDetails
